/**
 * Settings ("OPTIONS" channel) — web layout matches design/handegg-account.pen web-4-5: one sheet, three columns
 * of grouped section cards (not a left tab rail).
 *
 * Columns: ACCOUNT + PRIVACY · GAMEPLAY + VIDEO + CONTROLS + LEGAL · AUDIO + ABOUT.
 * ACCOUNT (sign in / profile card) · GAMEPLAY (name, netcode, touch) · AUDIO (bus volumes) · VIDEO (shake, CRT,
 * motion, F3) · CONTROLS (rebinding) · PRIVACY (analytics) · LEGAL · ABOUT.
 *
 * Navigation: ▲▼ across section headers (→ / confirm enters the section); inside a section ▲▼ moves, ◀▶ adjusts,
 * confirm toggles / rebinds, back returns to the section headers. Mouse/touch can hit any row directly.
 */
import { consentSignal, optIn, optOut } from '@hf/client-core/analytics';
import './settings.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { AUDIO_BUSES, type AudioBus } from '@hf/audio';
import type { ButtonName } from '@hf/shared';
import { DEVICE_ID } from '@hf/client-core/input/types';
import { webAnalyticsConfig } from '../../analytics/config';
import { useActions, useServices } from '../actions';
import { Head, Key } from '../components';
import { LAYER, useFocusGrid, useMenuLayer } from '../nav';
import {
  BUTTON_ROWS,
  isPadDevice,
  rebindableDevices,
  withOverride,
  withRebound,
  type RebindMove,
} from '@hf/client-core/ui-model/settings-apply';
import { DEFAULT_SETTINGS } from '@hf/client-core/ui-model/settings';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import type { Settings as SettingsT } from '@hf/client-core/ui-model/types';
import { cycle, Shell, StepRow } from './Shell';
import { linkAppleWeb, linkGoogleWeb, refreshWebAccount, webAccount } from '../wallet';
import { accountDialog } from '../overlays/AccountDialogs';
import { PORTAL } from '../../portal';
import { preloadAppleWeb, signInWithAppleWeb } from '../../net/apple-web-signin';
import { renderGoogleButton } from '../../net/google-web-signin';
import {
  sanitizeName,
  initialDeviceIndex,
  captureHints,
  SHAKE_STEPS,
} from '@hf/client-core/ui-model/screens';
export { sanitizeName, initialDeviceIndex, captureHints } from '@hf/client-core/ui-model/screens';

type Tab = 'account' | 'gameplay' | 'audio' | 'video' | 'controls' | 'privacy' | 'legal' | 'about';
const ALL_TABS: readonly { id: Tab; label: string; blurb: string }[] = [
  { id: 'account', label: 'ACCOUNT', blurb: 'Sign in with Apple or Google' },
  { id: 'gameplay', label: 'GAMEPLAY', blurb: 'Name, netcode, touch' },
  { id: 'audio', label: 'AUDIO', blurb: 'Music, crowd, announcer' },
  { id: 'video', label: 'VIDEO', blurb: 'Shake, CRT, motion, replays' },
  { id: 'controls', label: 'CONTROLS', blurb: 'Rebind every button' },
  { id: 'privacy', label: 'PRIVACY', blurb: 'Share gameplay stats' },
  { id: 'legal', label: 'LEGAL', blurb: 'Privacy policy · terms of use' },
  { id: 'about', label: 'ABOUT', blurb: 'Version · credit' },
];

/** Visual 3-column grouping from web-4-5 (Account|Privacy · Gameplay|…|Legal · Audio|About). Indices into TABS. */
const ALL_COL_TAB_IDS: readonly (readonly Tab[])[] = [
  ['account', 'privacy'],
  ['gameplay', 'video', 'controls', 'legal'],
  ['audio', 'about'],
];
/** Portal build: no account (sign-in) or analytics consent. */
const PORTAL_TABS: ReadonlySet<Tab> = new Set(['account', 'privacy']);
const TABS = PORTAL ? ALL_TABS.filter((t) => !PORTAL_TABS.has(t.id)) : ALL_TABS;
const COL_TAB_IDS = PORTAL
  ? ALL_COL_TAB_IDS.map((c) => c.filter((t) => !PORTAL_TABS.has(t))).filter((c) => c.length > 0)
  : ALL_COL_TAB_IDS;

const BUS_LABEL: Record<AudioBus, [string, string]> = {
  master: ['MASTER', 'Everything'],
  music: ['MUSIC', 'Menu and results themes'],
  sfx: ['EFFECTS', 'Hits, whistles, UI'],
  crowd: ['CROWD', 'Roars, gasps and boos'],
  announcer: ['ANNOUNCER', 'Play-by-play calls'],
};
const BUS_PREVIEW: Record<AudioBus, string> = {
  master: 'ui_confirm',
  music: 'ui_toggle',
  sfx: 'ui_slam',
  crowd: 'crowd_cheer',
  announcer: 'ui_confirm',
};

const SHAKE_NAME = (v: number): string => (v <= 0 ? 'OFF' : v < 0.75 ? 'LOW' : 'FULL');
const DELAYS: readonly (number | 'auto')[] = ['auto', 0, 1, 2, 3, 4, 5, 6];
const TOUCH_MODES: readonly SettingsT['touchControls'][] = ['auto', 'on', 'off'];

const lastTab = { current: 0 };

/** A Controls row is waiting for a key / button: the footer legend tells how to bind and how to cancel. */
const captureMode = signal<'pad' | 'kb' | null>(null);

/**
 * While a KEYBOARD capture waits, any connected pad's B or Start cancels it (the mirror of Esc cancelling a pad
 * capture): the input manager only resolves a keyboard capture with keys. Buttons already down when the capture
 * started (the A that opened it) are ignored until released.
 */
const usePadCancel = (on: boolean, cancel: () => void): void => {
  useEffect(() => {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (!on || !nav?.getGamepads) return;
    const CANCEL = [1, 9]; // standard mapping: B / Circle, Start / Options
    const read = (): boolean[] => {
      const out: boolean[] = [];
      let pads: (Gamepad | null)[] = [];
      try {
        pads = [...nav.getGamepads()];
      } catch {
        pads = [];
      }
      pads.forEach((p, i) => {
        for (const b of CANCEL) out[i * 32 + b] = !!p?.connected && !!p.buttons[b]?.pressed;
      });
      return out;
    };
    let prev = read();
    const t = setInterval(() => {
      const now = read();
      const hit = now.some((down, k) => down && !prev[k]);
      prev = now;
      if (hit) cancel();
    }, 50);
    return () => clearInterval(t);
  }, [on]);
};

export function Settings() {
  const actions = useActions();
  const { audio } = useServices();
  const [tab, setTab] = useState(lastTab.current);
  const [zone, setZone] = useState<'tabs' | 'panel'>('tabs');
  const back = (): void => {
    playSfx(audio, UI_SFX.back);
    actions.goto('main_menu');
  };
  const tabs = useFocusGrid({
    count: TABS.length,
    initial: tab,
    active: zone === 'tabs',
    onMove: (i) => {
      lastTab.current = i;
      setTab(i);
      playSfx(audio, UI_SFX.move);
    },
    onConfirm: (i) => {
      setTab(i);
      lastTab.current = i;
      setZone('panel');
      playSfx(audio, UI_SFX.confirm);
    },
    onAdjust: (_i, dir) => {
      if (dir === 1) setZone('panel');
    },
    onBack: back,
  });
  const renderPanel = (panelId: Tab, active: boolean) => {
    const props: PanelProps = {
      active,
      onExit: () => {
        setZone('tabs');
        playSfx(audio, UI_SFX.back);
      },
    };
    if (panelId === 'audio') return <AudioPanel {...props} />;
    if (panelId === 'video') return <VideoPanel {...props} />;
    if (panelId === 'gameplay') return <GameplayPanel {...props} />;
    if (panelId === 'controls') return <ControlsPanel {...props} />;
    if (panelId === 'privacy') return <PrivacyPanel {...props} />;
    if (panelId === 'legal') return <LegalPanel {...props} />;
    if (panelId === 'about') return <AboutPanel {...props} />;
    return <AccountPanel {...props} />;
  };
  return (
    <Shell
      screen="settings"
      title="SETTINGS"
      kicker="SETTINGS ARE SAVED ON THIS DEVICE"
      class="hf-settings"
      onBack={back}
      stadium="night"
      hints={
        captureMode.value
          ? captureHints(captureMode.value)
          : zone === 'tabs'
            ? [
                ['up|down', 'SECTION'],
                ['confirm', 'OPEN'],
                ['back', 'BACK'],
              ]
            : [
                ['up|down', 'SELECT'],
                ['left|right', 'ADJUST'],
                ['confirm', 'CHANGE'],
                ['back', 'SECTIONS'],
              ]
      }
    >
      <div class="hf-settings__sheet" data-testid="settings-sheet">
        <div class="hf-settings__cols" role="tablist" aria-label="Settings sections">
          {COL_TAB_IDS.map((col, colIdx) => (
            <div class="hf-settings__col" key={colIdx}>
              {col.map((secId) => {
                const i = TABS.findIndex((t) => t.id === secId);
                const t = TABS[i]!;
                const selected = tab === i;
                const open = selected && zone === 'panel';
                const head = tabs.item(i);
                return (
                  <section
                    key={t.id}
                    class={`hf-settings__sec ${selected ? 'selected' : ''} ${open ? 'active' : ''}`}
                    data-section={t.id}
                    onMouseDown={() => {
                      lastTab.current = i;
                      setTab(i);
                      setZone('panel');
                    }}
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      class={`hf-settings__sec-head interactive hf-settings__tab ${selected ? 'selected' : ''}`}
                      {...head}
                      onClick={(e) => {
                        head.onClick(e);
                      }}
                    >
                      <span class="t-display">{t.label}</span>
                      <small>{t.blurb}</small>
                    </button>
                    <div
                      class="hf-settings__sec-body"
                      role="tabpanel"
                      aria-hidden={!selected}
                      data-testid={`settings-panel-${t.id}`}
                    >
                      {renderPanel(t.id, open)}
                    </div>
                  </section>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}

interface PanelProps {
  active: boolean;
  onExit(): void;
}

function Slider({ value, onSet }: { value: number; onSet(v: number): void }) {
  const pct = Math.round(value * 100);
  return (
    <div class="hf-slider">
      <span
        class="hf-slider__track"
        role="slider"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        onClick={(e) => {
          e.stopPropagation();
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
          onSet(Math.round(((e.clientX - r.left) / Math.max(1, r.width)) * 10) / 10);
        }}
      >
        <span class="hf-slider__fill" style={{ width: `${pct}%` }} />
        <span class="hf-slider__ticks" />
      </span>
      <span class="hf-slider__val">{pct}</span>
    </div>
  );
}

function AudioPanel({ active, onExit }: PanelProps) {
  const { audio } = useServices();
  const v = ui.settings.value.volumes;
  const set = (bus: AudioBus, value: number): void => {
    const next = Math.min(1, Math.max(0, Math.round(value * 10) / 10));
    ui.updateSettings({ volumes: { ...ui.settings.peek().volumes, [bus]: next } });
    playSfx(audio, BUS_PREVIEW[bus] as never);
  };
  const grid = useFocusGrid({
    count: AUDIO_BUSES.length + 1,
    active,
    onAdjust: (i, dir) => {
      const bus = AUDIO_BUSES[i];
      if (bus) set(bus, v[bus] + dir * 0.1);
    },
    onConfirm: (i) => {
      if (i === AUDIO_BUSES.length) {
        ui.updateSettings({ volumes: { ...DEFAULT_SETTINGS.volumes } });
        playSfx(audio, UI_SFX.toggle);
      }
    },
    onBack: onExit,
  });
  return (
    <div class="hf-settings__rows">
      {AUDIO_BUSES.map((bus, i) => (
        <div
          class="hf-row interactive"
          key={bus}
          {...grid.item(i)}
          role="group"
          aria-label={BUS_LABEL[bus][0]}
        >
          <div class="hf-row__label">
            <span class="t-display">{BUS_LABEL[bus][0]}</span>
            <small>{BUS_LABEL[bus][1]}</small>
          </div>
          <Slider value={v[bus]} onSet={(x) => set(bus, x)} />
        </div>
      ))}
      <button class="hf-row hf-row--action interactive" {...grid.item(AUDIO_BUSES.length)}>
        <span class="t-display">RESET AUDIO</span>
      </button>
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  on,
  item,
}: {
  label: string;
  hint: string;
  on: boolean;
  item: Record<string, unknown>;
}) {
  return (
    <button class="hf-row interactive" {...item} role="switch" aria-checked={on}>
      <div class="hf-row__label">
        <span class="t-display">{label}</span>
        <small>{hint}</small>
      </div>
      <span class={`hf-toggle ${on ? 'on' : ''}`} aria-hidden="true" />
    </button>
  );
}

function VideoPanel({ active, onExit }: PanelProps) {
  const { audio } = useServices();
  const s = ui.settings.value;
  const rows: { kind: 'step' | 'toggle'; run(dir: -1 | 1): void }[] = [
    {
      kind: 'step',
      run: (dir) => {
        const cur = SHAKE_STEPS.reduce(
          (best, x) => (Math.abs(x - s.screenShake) < Math.abs(best - s.screenShake) ? x : best),
          1 as number,
        );
        ui.updateSettings({
          screenShake: cycle(SHAKE_STEPS, cur as (typeof SHAKE_STEPS)[number], dir, false),
        });
      },
    },
    { kind: 'toggle', run: () => ui.updateSettings({ crt: !s.crt }) },
    { kind: 'toggle', run: () => ui.updateSettings({ reducedMotion: !s.reducedMotion }) },
    { kind: 'toggle', run: () => ui.updateSettings({ showStats: !s.showStats }) },
    { kind: 'toggle', run: () => ui.updateSettings({ instantReplays: !s.instantReplays }) },
  ];
  const grid = useFocusGrid({
    count: rows.length,
    active,
    onAdjust: (i, dir) => {
      rows[i]?.run(dir);
      playSfx(audio, UI_SFX.toggle);
    },
    onConfirm: (i) => {
      rows[i]?.run(1);
      playSfx(audio, UI_SFX.toggle);
    },
    onBack: onExit,
  });
  return (
    <div class="hf-settings__rows">
      <StepRow
        label="SCREEN SHAKE"
        hint="Camera shake on hits and scores"
        value={SHAKE_NAME(s.screenShake)}
        focused={active && grid.index === 0}
        onStep={(d) => rows[0]!.run(d)}
        item={grid.item(0)}
      />
      <ToggleRow
        label="CRT FILTER"
        hint="Scanlines and glow, like Sunday TV in 1994"
        on={s.crt}
        item={grid.item(1)}
      />
      <ToggleRow
        label="REDUCED MOTION"
        hint="Calmer menus and titles, no flashing"
        on={s.reducedMotion}
        item={grid.item(2)}
      />
      <ToggleRow
        label="PERFORMANCE STATS"
        hint="Frame time and netcode overlay (F3 in a match)"
        on={s.showStats}
        item={grid.item(3)}
      />
      <ToggleRow
        label="INSTANT REPLAYS"
        hint="VHS replay of touchdowns and picks right after the play (any button skips)"
        on={s.instantReplays}
        item={grid.item(4)}
      />
    </div>
  );
}

const toggleAnalytics = (): void => (consentSignal.value === 'in' ? optOut('settings') : optIn('settings'));

function GameplayPanel({ active, onExit }: PanelProps) {
  const { audio, online } = useServices();
  const s = ui.settings.value;
  const nameRef = useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = useState(false);
  const commitName = (raw: string): void => {
    const name = sanitizeName(raw).trim();
    ui.updateSettings({ playerName: name });
    if (name && online.connected) {
      try {
        online.setName(name);
      } catch {
        /* offline */
      }
    }
  };
  const grid = useFocusGrid({
    count: 3,
    active: active && !editing,
    onAdjust: (i, dir) => {
      if (i === 1) ui.updateSettings({ inputDelay: cycle(DELAYS, s.inputDelay, dir, false) });
      else if (i === 2) ui.updateSettings({ touchControls: cycle(TOUCH_MODES, s.touchControls, dir) });
      else return;
      playSfx(audio, UI_SFX.toggle);
    },
    onConfirm: (i) => {
      if (i === 0) {
        setEditing(true);
        setTimeout(() => nameRef.current?.focus(), 0);
      } else if (i < 2) grid.setIndex(i + 1);
    },
    onBack: onExit,
  });
  return (
    <div class="hf-settings__rows">
      <div class="hf-row interactive" {...grid.item(0)} role="group" aria-label="Player name">
        <div class="hf-row__label">
          <span class="t-display">PLAYER NAME</span>
          <small>Shown on the scorebug and online (16 letters)</small>
        </div>
        <input
          ref={nameRef}
          class="hf-input t-broadcast"
          value={s.playerName}
          placeholder="PLAYER 1"
          maxLength={16}
          aria-label="Player name"
          onFocus={() => setEditing(true)}
          onInput={(e) => {
            const el = e.currentTarget;
            const clean = sanitizeName(el.value);
            if (clean !== el.value) el.value = clean;
          }}
          onBlur={(e) => {
            setEditing(false);
            commitName(e.currentTarget.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
        />
      </div>
      <StepRow
        label="ONLINE INPUT DELAY"
        hint="AUTO tunes it from your ping. Lower = snappier, more rollbacks"
        value={s.inputDelay === 'auto' ? 'AUTO' : `${s.inputDelay} FRAME${s.inputDelay === 1 ? '' : 'S'}`}
        focused={active && !editing && grid.index === 1}
        onStep={(d) => ui.updateSettings({ inputDelay: cycle(DELAYS, s.inputDelay, d, false) })}
        item={grid.item(1)}
      />
      <StepRow
        label="TOUCH CONTROLS"
        hint="On-screen pad for phones and tablets"
        value={s.touchControls.toUpperCase()}
        focused={active && !editing && grid.index === 2}
        onStep={(d) => ui.updateSettings({ touchControls: cycle(TOUCH_MODES, s.touchControls, d) })}
        item={grid.item(2)}
      />
    </div>
  );
}

function PrivacyPanel({ active, onExit }: PanelProps) {
  const { audio } = useServices();
  const consent = consentSignal.value;
  const grid = useFocusGrid({
    count: 1,
    active,
    onConfirm: () => {
      toggleAnalytics();
      playSfx(audio, UI_SFX.toggle);
    },
    onBack: onExit,
  });
  return (
    <div class="hf-settings__rows">
      <ToggleRow
        label="SHARE USAGE DATA"
        hint="Gameplay stats linked by a pseudonymous ID. Off unless you turn it on. No name, no chat, no ads"
        on={consent === 'in'}
        item={grid.item(0)}
      />
    </div>
  );
}

const LEGAL_LINKS: readonly { label: string; url: string }[] = [
  { label: 'Privacy Policy', url: 'https://handegg-legal.pages.dev/en/privacy' },
  { label: 'Terms of Use', url: 'https://handegg-legal.pages.dev/en/terms' },
];

function LegalPanel({ active, onExit }: PanelProps) {
  const { audio } = useServices();
  const grid = useFocusGrid({
    count: LEGAL_LINKS.length,
    active,
    onConfirm: (i) => {
      const link = LEGAL_LINKS[i];
      if (link) {
        playSfx(audio, UI_SFX.confirm);
        window.open(link.url, '_blank', 'noopener');
      }
    },
    onBack: onExit,
  });
  return (
    <div class="hf-settings__rows">
      {LEGAL_LINKS.map((link, i) => (
        <a
          key={link.url}
          class="hf-row interactive"
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          {...grid.item(i)}
        >
          <div class="hf-row__label">
            <span class="t-display">{link.label}</span>
          </div>
          <span aria-hidden="true">↗</span>
        </a>
      ))}
    </div>
  );
}

function AboutPanel({ active, onExit }: PanelProps) {
  // Nothing interactive to focus (version + copyright are read-only): a 1-item grid just carries Back → tabs.
  useFocusGrid({ count: 1, active, onBack: onExit });
  return (
    <div class="hf-settings__rows">
      <div class="hf-row" role="group" aria-label="Version">
        <div class="hf-row__label">
          <span class="t-display">VERSION</span>
        </div>
        <span class="t-broadcast">{webAnalyticsConfig().version}</span>
      </div>
      <p class="hf-about__copyright">© 2026 Amir Angel</p>
      <p class="hf-about__copyright">
        Fonts: Bungee, Fredoka (SIL OFL 1.1) · Luckiest Guy (Apache 2.0). Sound synth: ZzFX © Frank Force (MIT). See
        CREDITS.md.
      </p>
    </div>
  );
}

const LINK_NOTE = {
  offline: 'No connection — try again when you are online.',
  error: 'Could not sign in — try again.',
  merge_blocked: 'That Google account already has purchases on another profile. Contact support to merge.',
  unavailable: 'Sign-in is unavailable here.',
} as const;

/**
 * ACCOUNT: Google's own GIS button (popup) → ID token → POST /auth/link/google. Signing in keeps this browser's
 * coins and unlocks (the guest is linked) or switches to the account that already owns the Google identity.
 * Sign in with Apple JS (popup, Services ID com.hustlemania.handegg.web) → POST /auth/link/apple.
 */
function AccountPanel({ active, onExit }: PanelProps) {
  const actions = useActions();
  const host = useRef<HTMLDivElement | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const info = webAccount.value;
  const providers = info?.providers ?? [];
  const signedIn = providers.includes('google') || providers.includes('apple');
  const via = providers.includes('apple') ? 'Apple' : 'Google';
  // Signed in: 0 profile card (→ PROFILE screen) · 1 Sign out (→ SIGN OUT? confirm) · 2 Delete account (→ type-DELETE confirm).
  const grid = useFocusGrid({
    count: signedIn ? 3 : 1,
    initial: signedIn ? 1 : 0,
    active: active && accountDialog.value === null,
    onConfirm: (i) => {
      if (i === 0 && signedIn) actions.goto('profile');
      if (i === 1 && signedIn) accountDialog.value = 'signout';
      if (i === 2 && signedIn) accountDialog.value = 'delete';
    },
    onBack: onExit,
  });
  useEffect(() => {
    void refreshWebAccount();
    preloadAppleWeb();
  }, []);
  const done = (r: Awaited<ReturnType<typeof linkGoogleWeb>>, who: string): void => {
    setBusy(false);
    setNote(r === 'ok' ? `Signed in with ${who}.` : LINK_NOTE[r]);
  };
  useEffect(() => {
    const el = host.current;
    if (!el || signedIn) return;
    let live = true;
    renderGoogleButton(el, (tok) => {
      setBusy(true);
      setNote(null);
      void linkGoogleWeb(tok).then((r) => live && done(r, 'Google'));
    }).catch(() => live && setNote('Could not load Google sign-in — check your connection or ad blocker.'));
    return () => {
      live = false;
    };
  }, [signedIn]);
  const onApple = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    const s = await signInWithAppleWeb();
    if (!s.ok) {
      setBusy(false);
      setNote(
        s.reason === 'cancelled'
          ? 'Sign-in was cancelled.'
          : `Sign in with Apple failed — try again.${s.detail ? ` (${s.detail.slice(0, 60)})` : ''}`,
      );
      return;
    }
    done(await linkAppleWeb(s), 'Apple');
  };
  return (
    <div class="hf-settings__rows hf-account">
      {signedIn ? (
        <div
          class="hf-acct-card"
          {...grid.item(0)}
          role="group"
          aria-label="Account"
          data-testid="settings-account-card"
        >
          <Head character="rookie" expression="determined" class="hf-acct-card__head" />
          <div class="hf-acct-card__who">
            <small>Signed in as</small>
            <strong>{info?.name ?? 'Player'}</strong>
          </div>
          <span class="hf-acct-card__via">via {via}</span>
        </div>
      ) : (
        <div class="hf-row" {...grid.item(0)} role="group" aria-label="Account">
          <div class="hf-row__label">
            <span class="t-display">GUEST</span>
            <small>
              Sign in to keep your coins and unlocks across devices. Your current progress comes with you.
            </small>
          </div>
        </div>
      )}
      {signedIn && (
        <button
          type="button"
          class="hf-acct-row interactive"
          {...grid.item(1)}
          data-testid="settings-sign-out"
        >
          <span>⇥ Sign out</span>
          <span aria-hidden="true">›</span>
        </button>
      )}
      {signedIn && (
        <button
          type="button"
          class="hf-acct-row hf-acct-row--danger interactive"
          {...grid.item(2)}
          data-testid="settings-delete-account"
        >
          <span>🗑 Delete account</span>
          <span aria-hidden="true">›</span>
        </button>
      )}
      {!signedIn && (
        <button
          type="button"
          class="hf-account__apple"
          data-testid="apple-signin"
          disabled={busy}
          onClick={() => void onApple()}
          aria-label="Sign in with Apple"
        >
          <svg viewBox="0 0 814 1000" width="16" height="20" aria-hidden="true">
            <path
              fill="currentColor"
              d="M788 341c-6 4-108 62-108 190 0 148 130 200 134 202-1 3-21 72-69 142-43 62-88 124-156 124s-86-40-165-40c-77 0-104 41-166 41s-106-57-156-127C44 791 0 668 0 551 0 364 122 264 241 264c64 0 117 42 157 42 38 0 98-45 171-45 28 0 128 3 194 80zM554 168c30-36 51-85 51-134 0-7-1-14-2-20-49 2-107 33-142 73-27 31-53 80-53 130 0 8 1 15 2 18 3 1 9 2 14 2 44 0 99-30 130-69z"
            />
          </svg>
          <span>Sign in with Apple</span>
        </button>
      )}
      {!signedIn && (
        <div class="hf-account__google" data-testid="google-signin" ref={host} aria-busy={busy} />
      )}
      {note && (
        <p class="hf-account__note" role="status">
          {note}
        </p>
      )}
    </div>
  );
}

const rowName = (b: ButtonName): string => BUTTON_ROWS.find((r) => r.button === b)?.name ?? b;

/**
 * Rebind toast: the new key, plus what happened to the button that owned it ("JUMP → J · ACTION NOW W",
 * "… · TAKEN FROM ACTION", "… · ACTION IS UNBOUND"). Pure (tested).
 */
export const rebindToast = (
  button: ButtonName,
  code: string,
  moved: RebindMove | null,
  label: (code: string) => string,
): string => {
  const head = `${rowName(button)} → ${label(code)}`;
  if (!moved) return head;
  const other = rowName(moved.button);
  if (moved.now !== null) return `${head} · ${other} now ${label(moved.now)}`;
  if (moved.unbound) return `${head} · ${other} is unbound`;
  return `${head} · taken from ${other}`;
};

function ControlsPanel({ active, onExit }: PanelProps) {
  const { input, audio } = useServices();
  const [devices, setDevices] = useState(() => rebindableDevices(input));
  const [deviceIdx, setDeviceIdx] = useState(() =>
    initialDeviceIndex(
      devices,
      (() => {
        try {
          return input.lastActiveDevice();
        } catch {
          return null;
        }
      })(),
    ),
  );
  const [capturing, setCapturing] = useState<ButtonName | null>(null);
  const [version, setVersion] = useState(0);
  const [movedRow, setMovedRow] = useState<ButtonName | null>(null);
  const movedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abort = useRef<AbortController | null>(null);
  useEffect(() => input.onDevicesChanged(() => setDevices(rebindableDevices(input))), []);
  useEffect(
    () => () => {
      abort.current?.abort();
      captureMode.value = null;
      if (movedTimer.current) clearTimeout(movedTimer.current);
    },
    [],
  );
  const device = devices[Math.min(deviceIdx, devices.length - 1)] ?? {
    id: DEVICE_ID.KB_SOLO,
    label: 'KEYBOARD',
  };
  const bindings = input.bindingsFor(device.id);
  void version;
  usePadCancel(capturing !== null && !isPadDevice(device.id), () => abort.current?.abort());

  // While capturing, swallow router actions (the manager also suppresses menu events during a capture).
  useMenuLayer(() => {}, capturing !== null, LAYER.CAPTURE);

  const capture = async (button: ButtonName): Promise<void> => {
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    setCapturing(button);
    captureMode.value = isPadDevice(device.id) ? 'pad' : 'kb';
    playSfx(audio, UI_SFX.confirm);
    const code = await input.captureBinding(device.id, { timeoutMs: 8000, signal: ctl.signal });
    if (abort.current === ctl) abort.current = null;
    setCapturing(null);
    captureMode.value = null;
    if (!code) {
      playSfx(audio, UI_SFX.back);
      return;
    }
    const current = input.bindingsFor(device.id);
    const {
      next,
      bindings: nb,
      moved,
    } = withRebound(
      ui.settings.peek().bindings,
      device.id,
      current,
      input.defaultBindings(device.id),
      button,
      code,
    );
    input.setBindings(device.id, nb);
    ui.updateSettings({ bindings: next });
    setVersion((v) => v + 1);
    playSfx(audio, UI_SFX.slam);
    // Say what happened to the button that owned the key, and flash its row (QA r4: keys moved silently)
    if (movedTimer.current) clearTimeout(movedTimer.current);
    setMovedRow(moved?.button ?? null);
    if (moved) movedTimer.current = setTimeout(() => setMovedRow(null), 2400);
    ui.toast(
      rebindToast(button, code, moved, (c) => input.labelFor(device.id, c)),
      moved?.unbound ? 'info' : 'success',
      moved ? 3200 : 1800,
    );
  };
  const reset = (): void => {
    input.resetBindings(device.id);
    ui.updateSettings({
      bindings: withOverride(
        ui.settings.peek().bindings,
        device.id,
        input.defaultBindings(device.id),
        input.defaultBindings(device.id),
      ),
    });
    setVersion((v) => v + 1);
    playSfx(audio, UI_SFX.toggle);
    ui.toast(`${device.label}: defaults restored`, 'info', 1800);
  };
  const count = 1 + BUTTON_ROWS.length + 1;
  const grid = useFocusGrid({
    count,
    active: active && capturing === null,
    onAdjust: (i, dir) => {
      if (i !== 0 || devices.length < 2) return;
      setDeviceIdx((d) => (d + dir + devices.length) % devices.length);
      playSfx(audio, UI_SFX.toggle);
    },
    onConfirm: (i) => {
      if (i === 0) grid.setIndex(1);
      else if (i <= BUTTON_ROWS.length) void capture(BUTTON_ROWS[i - 1]!.button);
      else reset();
    },
    onBack: onExit,
  });
  const pad = isPadDevice(device.id);
  return (
    <div class="hf-settings__rows controls">
      <StepRow
        label="DEVICE"
        hint={
          pad
            ? 'Press a button or push a stick to bind · Hold B or press Start to cancel'
            : 'Press any key to bind · Esc cancels'
        }
        value={device.label}
        focused={active && grid.index === 0}
        onStep={(d) => setDeviceIdx((x) => (x + d + devices.length) % devices.length)}
        item={grid.item(0)}
      />
      {BUTTON_ROWS.map((r, i) => {
        const codes = bindings[r.button];
        const cap = capturing === r.button;
        return (
          <button
            class={`hf-row hf-bind interactive ${cap ? 'capturing' : ''} ${movedRow === r.button ? 'moved' : ''}`}
            key={r.button}
            {...grid.item(i + 1)}
          >
            <div class="hf-row__label">
              <span class="t-display">{r.name}</span>
              <small>{r.hint}</small>
            </div>
            <span class="hf-bind__keys">
              {cap ? (
                <span class="hf-bind__wait t-broadcast">{pad ? 'PRESS A BUTTON…' : 'PRESS A KEY…'}</span>
              ) : codes.length === 0 ? (
                <span class="t-muted">UNBOUND</span>
              ) : (
                codes.slice(0, 4).map((c) => <Key key={c} label={input.labelFor(device.id, c)} pad={pad} />)
              )}
            </span>
          </button>
        );
      })}
      <button class="hf-row hf-row--action interactive" {...grid.item(count - 1)}>
        <span class="t-display">RESET {pad ? 'PAD' : 'KEYS'} TO DEFAULTS</span>
      </button>
    </div>
  );
}
